import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getNewsShareUrl, getNewsSocialShareUrl } from "../utils/share";
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Modal,
    Dropdown,
    ButtonGroup,
} from "react-bootstrap";

const BASE = (() => {
    let url = process.env.REACT_APP_API_URL || "https://proudtapi.onrender.com";
    url = url.replace(/\/$/, "");
    if (url.endsWith("/api")) url = url.slice(0, -4);
    return url;
})();

const API_URL = `${BASE}/api/news`;

const getImageUrl = (item) => {
    const placeholder = "https://placehold.co/1200x650?text=No+Image";
    if (!item) return placeholder;

    const resolveUrl = (path) => {
        if (!path || typeof path !== "string" || path.trim() === "") return null;
        const p = path.trim();
        if (p.startsWith("http://") || p.startsWith("https://")) return p;
        if (p.startsWith("/uploads/")) return `${BASE}${p}`;
        if (p.startsWith("uploads/")) return `${BASE}/${p}`;
        if (p.includes(".")) return `${BASE}/uploads/${p}`;
        return null;
    };

    return (
        resolveUrl(item.imagePath) ||
        resolveUrl(item.image) ||
        resolveUrl(item.imageUrl) ||
        resolveUrl(item.imageURL) ||
        resolveUrl(item.thumbnail) ||
        placeholder
    );
};

const getDescription = (item) => {
    return (
        item.shortDesc ||
        item.description ||
        item.summary ||
        item.content ||
        "No description available."
    );
};

const getLongDescription = (item) => {
    return (
        item.content ||
        item.description ||
        item.summary ||
        item.shortDesc ||
        "No description available."
    );
};

const getViews = (item) => Number(item.views ?? item.totalViews ?? item.viewCount ?? 0);

const updateLocalViews = (items, id) => {
    return items.map((item) => {
        if (item._id === id || item.id === id) {
            return { ...item, views: getViews(item) + 1 };
        }
        return item;
    });
};

const incrementNewsViews = async (id) => {
    try {
        await fetch(`${API_URL}/views/${id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
        });
    } catch (err) {
        console.error("Unable to update news views:", err);
    }
};

const openNewTab = (url) => window.open(url, "_blank", "noopener,noreferrer");

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        alert("Link copied!");
    } catch {
        window.prompt("Copy this link:", text);
    }
};

const shareTo = (platform, item) => {
    if (!item) return;
    const url = platform === "copy" ? getNewsShareUrl(item) : getNewsSocialShareUrl(item);
    const title = (item.title || "News article").trim();
    const text = `${title}\n${url}`;
    switch (platform) {
        case "whatsapp":
            openNewTab(`https://wa.me/?text=${encodeURIComponent(text)}`);
            break;
        case "facebook":
            openNewTab(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
            break;
        case "x":
            openNewTab(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`);
            break;
        case "telegram":
            openNewTab(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`);
            break;
        case "linkedin":
            openNewTab(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
            break;
        case "email":
            openNewTab(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`);
            break;
        case "copy":
            copyToClipboard(url);
            break;
        default:
            break;
    }
};

const ShareMenu = ({ item, size = "sm" }) => (
    <Dropdown as={ButtonGroup} className="share-group">
        <Button size={size} variant="success" className="share-btn" onClick={() => shareTo("whatsapp", item)}>
            <i className="bi bi-share-fill" /> Share
        </Button>
        <Dropdown.Toggle split size={size} variant="success" />
        <Dropdown.Menu align="end" className="share-dropdown">
            <Dropdown.Item className="share-item whatsapp" onClick={() => shareTo("whatsapp", item)}>
                <i className="bi bi-whatsapp" /> WhatsApp
            </Dropdown.Item>
            <Dropdown.Item className="share-item facebook" onClick={() => shareTo("facebook", item)}>
                <i className="bi bi-facebook" /> Facebook
            </Dropdown.Item>
            <Dropdown.Item className="share-item x" onClick={() => shareTo("x", item)}>
                <i className="bi bi-twitter-x" /> X (Twitter)
            </Dropdown.Item>
            <Dropdown.Item className="share-item telegram" onClick={() => shareTo("telegram", item)}>
                <i className="bi bi-telegram" /> Telegram
            </Dropdown.Item>
            <Dropdown.Item className="share-item linkedin" onClick={() => shareTo("linkedin", item)}>
                <i className="bi bi-linkedin" /> LinkedIn
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item className="share-item email" onClick={() => shareTo("email", item)}>
                <i className="bi bi-envelope-fill" /> Email
            </Dropdown.Item>
            <Dropdown.Item className="share-item copy" onClick={() => shareTo("copy", item)}>
                <i className="bi bi-link-45deg" /> Copy Link
            </Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
);

const SearchResults = () => {
    const [searchParams] = useSearchParams();
    const query = searchParams.get("q") || "";
    const [articles, setArticles] = useState([]);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchResults = async () => {
            const trimmedQuery = query.trim();
            if (!trimmedQuery) {
                setArticles([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");

            try {
                const url = `${API_URL}?q=${encodeURIComponent(trimmedQuery)}&limit=100`;
                const res = await fetch(url);
                const result = await res.json();
                setArticles(Array.isArray(result?.data) ? result.data : []);
            } catch (err) {
                setError("Unable to load search results. Please try again.");
                console.error("Search fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [query]);

    const handleReadMore = async (item) => {
        const id = item?._id || item?.id;
        if (!id) return;
        setArticles((prev) => updateLocalViews(prev, id));
        setSelectedArticle({ ...item, views: getViews(item) + 1 });
        await incrementNewsViews(id);
    };

    return (
        <Container className="py-4">
            <div className="d-flex align-items-center justify-content-between mb-4 flex-column flex-md-row gap-3">
                <div>
                    <h2 className="mb-1">Search Results</h2>
                    <p className="text-muted">
                        {query
                            ? `Showing results for "${query.trim()}"`
                            : "Type a keyword in the search bar to find news articles."}
                    </p>
                </div>
                <Link to="/" className="btn btn-outline-secondary">
                    Back to Home
                </Link>
            </div>

            {loading ? null : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : !query.trim() ? (
                <div className="alert alert-info">Enter a search term in the header to begin.</div>
            ) : articles.length === 0 ? (
                <div className="alert alert-warning">No news articles found for "{query.trim()}".</div>
            ) : (
                <Row xs={1} md={2} lg={3} className="g-4">
                    {articles.map((item) => {
                        const description = getDescription(item);
                        const preview = description.length > 140 ? `${description.slice(0, 140).trim()}...` : description;
                        const views = getViews(item);
                        return (
                            <Col key={item._id || item.id}>
                                <Card className="h-100 shadow-sm">
                                    <div className="ratio ratio-16x9 overflow-hidden">
                                        <Card.Img
                                            variant="top"
                                            src={getImageUrl(item)}
                                            alt={item.title || "Thumbnail"}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://placehold.co/1200x650?text=Image+Not+Found";
                                            }}
                                            className="object-fit-cover"
                                        />
                                    </div>
                                    <Card.Body className="d-flex flex-column">
                                        <Badge bg="secondary" className="mb-2 text-uppercase">
                                            {item.category || "News"}
                                        </Badge>
                                        <Card.Title>{item.title || "Untitled"}</Card.Title>
                                        <div className="mb-3 d-flex align-items-center justify-content-between gap-2 text-muted">
                                            <span>
                                                <span role="img" aria-label="views">👁️</span> {views} views
                                            </span>
                                            <ShareMenu item={item} size="sm" />
                                        </div>
                                        <Card.Text className="mb-3 news-description">{preview}</Card.Text>
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-auto">
                                            <small className="text-muted">{new Date(item.createdAt).toLocaleDateString()}</small>
                                            <Button
                                                as={Link}
                                                to={`/news-details/${item._id || item.id}`}
                                                variant="primary"
                                                size="sm"
                                            >
                                                Read More
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            <Modal
                show={Boolean(selectedArticle)}
                onHide={() => setSelectedArticle(null)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedArticle?.title || "Article details"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedArticle && (
                        <>
                            <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
                                <Badge bg="secondary" className="text-uppercase">{selectedArticle.category || "News"}</Badge>
                                <small className="text-muted d-flex align-items-center">
                                    <span className="me-1" role="img" aria-label="views">👁️</span>
                                    {selectedArticle.views} views
                                </small>
                                <small className="text-muted">{new Date(selectedArticle.createdAt).toLocaleDateString()}</small>
                            </div>
                            <div className="ratio ratio-16x9 overflow-hidden mb-3">
                                <img
                                    src={getImageUrl(selectedArticle)}
                                    alt={selectedArticle.title || "Article image"}
                                    className="object-fit-cover w-100 h-100"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://placehold.co/1200x650?text=Image+Not+Found";
                                    }}
                                />
                            </div>
                            <h6>Full Description</h6>
                            <p className="news-article-text">{getLongDescription(selectedArticle)}</p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedArticle(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default SearchResults;
